import React, { useEffect } from "react"
import { Box, Skeleton, styled } from "@mui/material"

import { colors } from "theme"
import { useAppDispatch, useAppSelector } from "redux/hooks"
import { fetchSubscriptions } from "redux/features/subscription/subscriptionThunk"
import {
  selectFetchCollectionStatus,
  selectSubscriptions,
  selectTotal,
} from "redux/features/subscription/subscriptionSlice"
import LoadableCollection from "components/ui/LoadableCollection"
import { SubscriptionListType } from "types"

export function withSubscriptions<GenericType>(
  WrappedComponent: React.ComponentType<GenericType>,
) {
  const WithSubscriptions = (props: GenericType) => {
    const dispatch = useAppDispatch()

    const total = useAppSelector((state) => selectTotal(state))
    const subscriptions = useAppSelector((state) =>
      selectSubscriptions(state),
    ) as SubscriptionListType
    const fetchStatus = useAppSelector((state) =>
      selectFetchCollectionStatus(state),
    )

    const handleFetchSubscriptions = () => {
      if (!fetchStatus.isFetching) {
        dispatch(fetchSubscriptions())
      }
    }

    useEffect(() => handleFetchSubscriptions(), [])

    const renderSkeleton = () => {
      return (
        <Container>
          <Card sx={{ height: 250 }} animation="pulse" variant="rectangular" />
        </Container>
      )
    }

    return (
      <LoadableCollection
        fetchStatus={fetchStatus}
        renderLoading={renderSkeleton}
      >
        {() => (
          <WrappedComponent
            {...props}
            total={total}
            subscriptions={subscriptions}
            fetchStatus={fetchStatus}
            fetchDebts={handleFetchSubscriptions}
          />
        )}
      </LoadableCollection>
    )
  }

  return WithSubscriptions
}

const Container = styled(Box)`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  align-items: center;
  margin: 42px 0 0 0;
`

const Card = styled(Skeleton)`
  background-color: ${colors.cultured};
  box-shadow: 12px 14px 23px -2px rgb(0 0 0 / 3%);
  border-radius: 26px;
  margin-bottom: 16px;
  padding: 58px;
  width: 100%;
`
